import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "sv_storage_warn_dismissed_until";
// Re-show the dismissed banner after 6 hours so the admin doesn't go indefinitely blind
const DISMISS_MS = 6 * 60 * 60 * 1000;

/**
 * Shows a maroon warning banner across every admin page when MongoDB usage
 * crosses 80% of the Atlas free tier. Dismissable for 6 hours per session.
 * Fetches /api/admin/storage-stats once per mount — cheap, indexed query.
 */
export default function StorageWarningBanner() {
  const [stats, setStats] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    try {
      const until = Number(sessionStorage.getItem(DISMISS_KEY) || 0);
      if (until > Date.now()) {
        setHidden(true);
        return;
      }
    } catch { /* ignore */ }
    api.get("/admin/storage-stats")
      .then(({ data }) => setStats(data))
      .catch(() => { /* admin-only endpoint will 401/403 for non-admins; silent */ });
  }, []);

  if (hidden || !stats) return null;
  const pct = stats.mongo?.atlas_free_percent || 0;
  if (pct < 80) return null;

  const critical = pct >= 95;
  const bg = critical ? "bg-[#D62828] text-white" : "bg-[#FEF3C7] text-[#D62828]";
  const accent = critical ? "text-[#FFD27A]" : "text-[#D62828]";

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS)); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <div className={`${bg} border-b border-[#F4A100]/40 px-4 md:px-8 py-2.5 flex items-center gap-3 sv-anim-in`} data-testid="storage-warning-banner">
      <AlertTriangle size={16} className={accent} />
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-medium">
          {critical ? "MongoDB nearly full" : "MongoDB approaching capacity"}
        </span>
        <span className={`ml-2 ${critical ? "text-white/85" : "text-[#4A4A4A]"}`}>
          · {pct}% of the 512 MB Atlas free tier used.{" "}
          {critical ? "Upgrade or clean up old data soon." : "Plan an upgrade before you hit 100%."}
        </span>{" "}
        <Link to="/admin/storage" className={`underline ${critical ? "text-[#FFD27A]" : "text-[#D62828]"}`} data-testid="storage-warning-link">
          View storage →
        </Link>
      </div>
      <button
        onClick={dismiss}
        type="button"
        className={`p-1 ${critical ? "text-white/70 hover:text-white" : "text-[#4A4A4A] hover:text-[#D62828]"}`}
        aria-label="Dismiss"
        data-testid="storage-warning-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
